import {entriesOf, groupIsIncluded, isMultiAccountGroup} from './clientGroups';

/**
 * Qué filas del paso 3 se ven, de todas las que el usuario tiene.
 *
 * La propuesta de una agencia grande no es una lista para leer: son cientos de decisiones, y la
 * mayoría son merges esperando un gesto. Pintarlas todas de una vez costaba 11.400 nodos de DOM
 * —medido sobre un padrón de 638 filas— y enterraba el trabajo real entre filas que no lo pedían.
 *
 * Esto es la LENTE, y vive afuera de React porque es donde está la regla que no se puede romper:
 *
 * ⚠️ **Filtrar y paginar no deciden nada.** Lo que se va a crear sale siempre de la lista completa
 * (`includedGroupsOf`), nunca de lo que hay en pantalla. Si el conteo del botón saliera de las filas
 * visibles, buscar «Rayovac» haría que «Continuar» prometiera un cliente y el paso siguiente
 * mostrara trescientos. Por eso acá no hay ninguna función que cuente clientes: sólo dicen qué se
 * MUESTRA, y el contador de la pantalla no las usa.
 */

/** Cuántas filas se pintan por tanda. 100 filas ≈ 1.800 nodos: el mount se siente inmediato. */
export const ROWS_PER_PAGE = 100;

/**
 * Desde cuántas filas aparece la barra de búsqueda y filtros.
 *
 * Con una cartera chica el muro no existe, y una barra de herramientas sobre doce filas es ruido
 * que hace parecer complicado algo que no lo es. El umbral es de PRESENTACIÓN: el paginado corre
 * siempre, porque su costo es nulo cuando hay menos filas que una tanda.
 */
export const TOOLBAR_MIN_ROWS = 30;

export const FILTER_ALL = 'all';
export const FILTER_UNCONFIRMED = 'unconfirmed';
export const FILTER_INCLUDED = 'included';
export const FILTER_DOUBTS = 'doubts';
/**
 * Lo que quedó fuera de la cartera: dormidas, cerradas y sin dueño.
 *
 * Es la lente que le faltaba a la barra, y la que vuelve honestas a las otras cuatro. Hasta acá los
 * filtros gobernaban SÓLO la lista de arriba y el bloque de revisión seguía mostrando sus
 * ochocientas filas pasara lo que pasara: Pablo filtró por «listos para crear» esperando ver sus
 * cuatro elegidos y no se movió nada — «me quedé en la de revisión».
 */
export const FILTER_BUCKET = 'bucket';

export const FILTERS = [FILTER_ALL, FILTER_UNCONFIRMED, FILTER_INCLUDED, FILTER_DOUBTS, FILTER_BUCKET];

/**
 * Texto comparable: sin acentos, sin mayúsculas y sin espacios de más.
 *
 * Buscar «ferreteria» tiene que encontrar «Ferretería Central». Los nombres los escribieron personas
 * en la plataforma de anuncios, así que la acentuación es exactamente lo que no se puede dar por
 * sentado.
 */
const COMBINING_MARKS = new RegExp('[\\u0300-\\u036f]', 'g');

export function normalizeForSearch(text) {
    return String(text === null || text === undefined ? '' : text)
        .normalize('NFD')
        .replace(COMBINING_MARKS, '')
        .toLowerCase()
        .trim();
}

/**
 * ¿Alguno de estos textos responde a lo que el usuario escribió?
 *
 * Primero la frase entera, que es el caso normal. Si no aparece tal cual, alcanza con que CADA
 * palabra esté en alguno de los NOMBRES: Leo buscó «perfumerías rouge» y no apareció nada, porque
 * el cliente se llamaba «Rouge» y ninguna cuenta decía la frase exacta (card 03NF0The). Con las
 * palabras sueltas, el nombre del cliente y el de la cuenta se complementan. Sigue siendo un «y»:
 * una palabra que no está en ningún lado deja la fila afuera.
 *
 * ⚠️ Los ids de plataforma (`ids`) sólo cuentan con la frase entera. Con las palabras sueltas, el
 * «0007» de «Cliente 0007» encontraba cualquier cuenta cuyo número tuviera esos cuatro dígitos
 * adentro: un id es una cifra que se busca entera, no una palabra.
 */
function textsMatch(names, ids, needle) {
    const normalizedNames = names.map(normalizeForSearch);
    const everything = normalizedNames.concat(ids.map(normalizeForSearch));
    if (everything.some(text => text.includes(needle))) return true;
    const words = needle.split(/\s+/).filter(Boolean);
    return words.length > 1 && words.every(word => normalizedNames.some(text => text.includes(word)));
}

/**
 * ¿Esta fila responde a lo que el usuario escribió?
 *
 * Busca por el nombre del cliente y también por el de sus cuentas y su id de plataforma: el usuario
 * llega a esta pantalla desde el administrador de anuncios, donde lo que tiene a mano es el nombre
 * de la cuenta —o su número—, no el del cliente que Dashbo dedujo.
 */
export function groupMatchesQuery(group, needle) {
    if (!needle) return true;
    const names = [group.name];
    const ids = [];
    entriesOf(group).forEach(entry => {
        const account = entry.account || {};
        names.push(account.name);
        ids.push(account.account_id);
    });
    return textsMatch(names, ids, needle);
}

/**
 * ¿Esta cuenta del cajón responde a la búsqueda?
 *
 * Además de su nombre y su id, mira el CLIENTE que tiene asociado: el que le sugerimos («Crear
 * «Iga»»), el del que la sacó el usuario y el que ya tiene en la base. Es el mismo criterio que la
 * lista de arriba, donde buscar el nombre del cliente trae sus cuentas; en el cajón sólo se miraba
 * el nombre de la cuenta, así que la misma palabra encontraba el cliente arriba y no su cuenta abajo.
 */
export function entryMatchesQuery(entry, needle) {
    if (!needle) return true;
    const account = entry?.account || {};
    const decision = entry?.decision || {};
    return textsMatch([account.name, decision.proposedName, decision.unassignedFrom, entry?.clientName],
                      [account.account_id], needle);
}

/**
 * Las entradas del CAJÓN que coinciden con la búsqueda.
 *
 * Vive acá y no en el componente del cajón porque la respuesta la necesitan los dos: el cajón para
 * saber qué pintar, y la pantalla para poder decir «acá arriba no hay nada, pero tu cuenta está
 * abajo» en vez de un «ningún cliente coincide» a 200 px de la fila que el usuario busca. Con el
 * criterio escrito dos veces, esas dos superficies podían contestar distinto sobre la misma cuenta.
 */
export function bucketEntriesMatching(entries, query) {
    const needle = normalizeForSearch(query);
    if (!needle) return entries || [];
    return (entries || []).filter(entry => entryMatchesQuery(entry, needle));
}

/** ¿Esta fila está esperando el gesto del usuario? Es el complemento exacto de «se va a crear». */
export function groupIsUnconfirmed(group) {
    return !groupIsIncluded(group);
}

/** ¿Esta fila tiene alguna pregunta sin responder? */
export function groupHasDoubts(group) {
    return (group.doubts || []).length > 0;
}

const PREDICATES = {
    [FILTER_ALL]: () => true,
    [FILTER_UNCONFIRMED]: groupIsUnconfirmed,
    [FILTER_INCLUDED]: groupIsIncluded,
    [FILTER_DOUBTS]: groupHasDoubts,
    // Ninguna fila de la cartera pasa esta lente: lo que muestra vive en el cajón, no acá.
    [FILTER_BUCKET]: () => false,
};

/**
 * ¿Esta lente alcanza al bloque de revisión?
 *
 * Las cuentas del cajón no son clientes propuestos: ninguna está «lista para crear» ni tiene dudas
 * que responder, así que las tres lentes de la cartera lo dejan vacío — y eso es exactamente lo que
 * el usuario espera al filtrar. La simetría es la que hace que la barra se entienda: cada lente
 * elige qué PARTE de la pantalla es relevante, y las dos zonas obedecen a la misma.
 */
export function bucketMatchesFilter(filter) {
    return filter === FILTER_ALL || filter === FILTER_BUCKET;
}

/**
 * Cuántas filas hay de cada clase.
 *
 * Los cuatro números viajan a los botones de filtro para que el usuario sepa qué le espera detrás de
 * cada uno ANTES de apretarlo. Un filtro que puede devolver una lista vacía sin avisar es un botón
 * que hace dudar de si funcionó.
 *
 * No son una partición: una fila con dudas puede además estar incluida. Son vistas, no cajones.
 *
 * **Respetan la búsqueda** (`query`): con «rayovac» escrito, «Listos para crear (40)» prometía
 * cuarenta filas detrás de un botón que mostraba una. El número de cada botón es lo que ese botón
 * va a mostrar con la búsqueda que hay puesta. El del cajón llega ya filtrado desde afuera, por la
 * misma razón por la que llega desde afuera (ver abajo).
 */
export function countsOf(groups, bucketCount = 0, query = '') {
    const needle = normalizeForSearch(query);
    const rows = (groups || []).filter(group => groupMatchesQuery(group, needle));
    return {
        [FILTER_ALL]: rows.length,
        [FILTER_UNCONFIRMED]: rows.filter(groupIsUnconfirmed).length,
        [FILTER_INCLUDED]: rows.filter(groupIsIncluded).length,
        [FILTER_DOUBTS]: rows.filter(groupHasDoubts).length,
        // Viene de afuera: el cajón no son filas de esta lista, y contarlo acá con `rows` daría
        // siempre cero — un botón que promete un conjunto vacío y esconde ochocientas cuentas.
        [FILTER_BUCKET]: bucketCount,
    };
}

/**
 * En qué orden se recorre la propuesta.
 *
 * El orden que trae el backend es el del agrupamiento, que no dice nada sobre dónde está el trabajo.
 * Con cientos de filas, lo primero que se ve es lo único que mucha gente va a mirar, así que arriba
 * va lo que pide una decisión y lo que sale caro si está mal:
 *
 * 1. Las filas con dudas sin responder, que son las que esperan un gesto explícito.
 * 2. El resto por cantidad de cuentas, de mayor a menor. La precisión del agrupamiento medida
 *    contra producción es 0,78 —uno de cada cinco merges está mal— y el costo del error crece con
 *    el tamaño: juntar mal ocho cuentas son veintiocho pares equivocados, juntar mal dos es uno.
 *    Un grupo de una sola cuenta no tiene ninguna decisión de agrupación que revisar, y por eso
 *    queda al final.
 * 3. El nombre ORIGINAL (`sortName`), para que dos filas equivalentes no se intercambien de lugar
 *    entre renders. El original y no el que se ve: renombrar una fila la mandaba a otra letra.
 *
 * Es orden de PRESENTACIÓN: igual que filtrar y paginar, no cambia lo que se va a crear.
 */
export function sortedForReview(groups) {
    return (groups || []).slice().sort((left, right) => {
        const byDoubts = Number(groupHasDoubts(right)) - Number(groupHasDoubts(left));
        if (byDoubts !== 0) return byDoubts;

        const bySize = entriesOf(right).length - entriesOf(left).length;
        if (bySize !== 0) return bySize;

        return String(left.sortName ?? left.name ?? '').localeCompare(String(right.sortName ?? right.name ?? ''));
    });
}

/**
 * Las filas que se pintan, y lo que hace falta decir sobre las que no.
 *
 * Devuelve `{rows, matching, total, hidden}`:
 * - `rows` — lo que se renderiza.
 * - `matching` — cuántas pasan el filtro y la búsqueda (el denominador del «mostrando X de Y»).
 * - `total` — cuántas tiene la propuesta entera, mire el usuario lo que mire.
 * - `hidden` — cuántas quedaron detrás de «Mostrar más».
 *
 * `total` viaja aunque parezca redundante: es lo que permite decir «ninguna de tus 638 coincide» en
 * vez de un «no hay resultados» que se lee como que la propuesta se perdió.
 *
 * `sequence` es el orden ENTERO (secciones incluidas, sin cortar): dice en qué posición va a caer
 * una fila aunque todavía no esté pintada. `splitAt` es dónde empieza la sección de pendientes entre
 * las filas pintadas, y `showSections` si corresponde dibujar los dos encabezados.
 *
 * Con `frozen` (ver `freezeOf`), las filas se quedan donde estaban cuando se abrió el editor.
 */
export function visibleRowsOf({groups, query, filter, visibleCount, frozen = null}) {
    const all = groups || [];
    const predicate = PREDICATES[filter] || PREDICATES[FILTER_ALL];
    const needle = normalizeForSearch(query);

    const matched = needle || predicate !== PREDICATES[FILTER_ALL]
        ? all.filter(group => predicate(group) && groupMatchesQuery(group, needle))
        : all;

    // El orden va ANTES del corte: si se ordenara sólo la tanda visible, «Mostrar más» traería
    // filas más importantes que las que ya están arriba y el usuario tendría que releer todo.
    const ordered = sortedForReview(matched);

    // Y por ENCIMA de ese orden van las dos secciones. La decisión del 04/09 —lo que necesita
    // trabajo primero— es correcta dentro de un bloque de veinte filas y se vuelve confusa en uno
    // de doscientas: Pablo abrió su propuesta y vio doscientas filas apagadas, con lo que le sirve
    // enterrado abajo. «Lo lógico es que primero aparezcan las que sí». La sección conserva las dos
    // cosas: arriba lo que ya está resuelto, abajo lo que pide una decisión, y ADENTRO de cada una
    // sigue mandando el orden por trabajo pendiente.
    const ready = ordered.filter(group => groupIsIncluded(group));
    const pending = ordered.filter(group => !groupIsIncluded(group));
    const bySection = ready.concat(pending);

    // Un `visibleCount` inválido (0, negativo, NaN) dejaría la pantalla en blanco sin decir por qué.
    const limit = Number.isFinite(visibleCount) && visibleCount > 0 ? visibleCount : ROWS_PER_PAGE;
    const rows = bySection.length > limit ? bySection.slice(0, limit) : bySection;

    // Los contadores son de la SECCIÓN ENTERA, no de lo que entró en la tanda: el encabezado dice
    // cuántas hay, y el «Mostrar más» de abajo dice cuántas faltan pintarse. Dos preguntas
    // distintas.
    const live = {rows, matched: ordered, sequence: bySection, matching: matched.length, total: all.length,
                  readyCount: ready.length, pendingCount: pending.length,
                  splitAt: ready.length, showSections: ready.length > 0 && pending.length > 0,
                  hidden: matched.length - rows.length};
    return frozen ? withFrozenOrder(live, all, frozen, limit) : live;
}

/**
 * La foto del orden en el momento en que se abre un editor de cliente.
 *
 * **Mientras hay un editor abierto, las filas no se mueven** (card 03NF0The). El orden depende de lo
 * que el editor cambia —el tamaño del grupo, si está elegido—, así que cada «Sacar de este cliente»
 * reordenaba la lista bajo el cursor: la fila de Bakián pasó de la 1.ª a la 4.ª, y con la segunda
 * cuenta desapareció de la pantalla con el editor abierto, unas 30 filas más abajo. «Se pierde la
 * pantalla», dijo fede mirando.
 *
 * La foto guarda el orden ENTERO de la lente (`keys`, no sólo las filas pintadas: «Mostrar más» con
 * un editor abierto destapa las siguientes de la foto y no reordena nada), cuáles caían en la sección
 * de listos (`ready`), qué grupos existían (`known`, para reconocer los que nacen mientras tanto) y la
 * fila que se está editando (`anchor`, donde se ubican los que nacen). Al cerrar el editor la foto se
 * descarta y la lista vuelve a su orden: la pantalla lleva la fila a la vista.
 */
export function freezeOf(visible, groups, anchor) {
    const sequence = visible?.sequence || visible?.rows || [];
    return {
        keys: sequence.map(group => group.key),
        ready: new Set(sequence.slice(0, visible?.readyCount || 0).map(group => group.key)),
        known: new Set((groups || []).map(group => group.key)),
        anchor,
        showSections: Boolean(visible?.showSections),
    };
}

/**
 * La foto, con los grupos que nacieron desde que se tomó ya ubicados para siempre.
 *
 * Lo que nace (la cuenta que se separó en su propio cliente) se ubica debajo de la fila que se está
 * editando EN ESE MOMENTO, y ahí se queda. Antes se recalculaba en cada render contra el ancla del
 * momento: abrir el editor del cliente recién separado —para darle «Listo», por ejemplo— lo mandaba
 * al final de la lista con su editor abierto (review C-paso3-01). La pantalla lo llama apenas ve una
 * clave nueva, así que entre el nacimiento y la adopción no hay ningún render con otro ancla.
 */
export function adoptBorn(frozen, bornKeys) {
    const fresh = (bornKeys || []).filter(key => !frozen.known.has(key));
    if (!fresh.length) return frozen;
    const at = frozen.keys.indexOf(frozen.anchor);
    const keys = at < 0 ? frozen.keys.concat(fresh)
        : frozen.keys.slice(0, at + 1).concat(fresh, frozen.keys.slice(at + 1));
    const ready = new Set(frozen.ready);
    if (frozen.ready.has(frozen.anchor)) fresh.forEach(key => ready.add(key));
    return {...frozen, keys, ready, known: new Set([...frozen.known, ...fresh])};
}

/**
 * Las filas en el orden de la foto, con los datos de ahora.
 *
 * Lo que la foto tenía y ya no existe (un cliente que se quedó sin cuentas) se cae; lo que nació y
 * todavía no se adoptó (ver `adoptBorn`) va debajo de la fila que se está editando. Los números de
 * la cartera (`total`, los de cada sección, `matched` para el tilde maestro) siguen siendo los de
 * ahora: la foto congela DÓNDE se dibuja cada fila, no lo que se cuenta. Se pagina igual que la
 * lista viva: `limit` filas.
 *
 * `matching` y `hidden` sí son los de la foto: dicen «mostrando X de Y» sobre lo que se DIBUJA. Con
 * los vivos, sacarle a un cliente buscado la cuenta que coincidía decía «mostrando 1 de los 0».
 */
function withFrozenOrder(live, all, frozen, limit) {
    const adopted = adoptBorn(frozen, all.map(group => group.key));
    const byKey = new Map(all.map(group => [group.key, group]));
    const ordered = adopted.keys.map(key => byKey.get(key)).filter(Boolean);
    const rows = ordered.length > limit ? ordered.slice(0, limit) : ordered;
    const splitAt = rows.filter(group => adopted.ready.has(group.key)).length;

    return {...live, rows, splitAt, matching: ordered.length, hidden: ordered.length - rows.length,
            showSections: frozen.showSections && splitAt > 0 && splitAt < rows.length};
}

/**
 * Sobre qué actúa una acción masiva, y qué está por aprobar.
 *
 * **El alcance son las filas que COINCIDEN con la lente, no las visibles ni el total.** Las tres
 * opciones son defendibles en abstracto y sólo una es honesta: sobre la tanda visible, «seleccionar
 * todas» dejaría afuera filas que el usuario cree haber elegido; sobre el total, tildaría
 * seiscientas mientras la pantalla muestra treinta y siete. Con la lente, buscar pasa a ser la forma
 * de acotar la acción —«rayovac» y listo— que es justamente lo que vuelve útil un botón así.
 *
 * `merges` es la parte incómoda y por eso viaja: son los grupos de dos o más cuentas que la acción
 * está por dar por buenos sin que nadie los haya mirado. El botón lo dice antes de que se apriete.
 */
export function selectionOf(matchedGroups) {
    const rows = matchedGroups || [];
    const pending = rows.filter(group => !groupIsIncluded(group));
    return {
        matching: rows.length,
        ticked: rows.length - pending.length,
        merges: pending.filter(isMultiAccountGroup).length,
        // Las cuentas en DUDA cuentan igual que las demás. Juntaban sólo `group.accounts`, así que
        // una fila cuyas cuentas están todas en duda tiene `accounts: []` y el tilde maestro no la
        // tocaba: quedaba idéntica después de apretar «Seleccionar todo». Con una cartera dominada
        // por merges eso se ve como un botón que no hace nada — Pablo lo intentó varias veces con
        // «Todos (246)». Y es incoherente con `merges`, que sí las cuenta para el aviso.
        pks: rows.flatMap(group => entriesOf(group).map(entry => entry.pk)),
    };
}


